import { TagSegmentColorType, TagSegmentSlim } from '@/features/tagSegments';

export interface SegmentListItemReadModel {
  id: string;
  name: string;
  color?: string;
}

export enum CustomerTypeEnum {
  Unidentified = 'Unidentified',
  Prospective = 'Prospective',
  Identified = 'Identified',
}

export interface CustomerListItemReadModel {
  id: string;
  documentNo: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  taxCode?: string;
  phoneNumbers: string[];
  email?: string;
  customerType: CustomerTypeEnum;
  accountNumber?: string;
  eicCode?: string;
  status?: string;
  address?: string;
  postalCode?: string;
  debt?: number;
  overpayment?: number;
  segments: TagSegmentSlim[];
}

export interface CustomerInfoReadModel {
  id: string;
  passportNumber: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  passportSeries?: string;
  tin: string;
  email: string;
  phoneNumbers: string[];
  customerType: CustomerTypeEnum;
}

export interface ContactListItemReadModel {
  id: string;
  fullName: string;
  phoneNumbers: string[];
  email?: string;
  digitalServices: string[];
  communicationConsent: boolean;
}

export interface PersonalAccountEquipmentReadModel {
  equipmentId: string;
  equipmentGroupId: string;
  equipmentGroupName?: string;
  equipmentTypeId: string;
  equipmentTypeName?: string;
  factoryNumber: string;
}

export interface PersonalAccountMeteringDeviceReadModel {
  meteringDeviceId: string;
  meteringDeviceGroup: string;
  meteringDeviceGroupName?: string;
  meteringDeviceTypeId: string;
  meteringDeviceTypeName?: string;
  factoryNumber: string;
}

export interface PersonalAccountReadModel {
  accountNumber: string;
  status: string;
  eicCode: string;
  address: string;
  postalCode?: string | null;
  hasMeter: boolean;
  consumptionGroupType: number;
  contractDate: string;
  balance: number;
  equipments: PersonalAccountEquipmentReadModel[];
  meteringDevices: PersonalAccountMeteringDeviceReadModel[];
}

/** GET /api/customers/{id}/metering-points — the customer's metering points (особові рахунки), oldest ownership first (GSB-8497). */
export interface CustomerMeteringPointReadModel {
  meteringPointId: string;
  accountNumber: string;
  eicCode: string;
  address: string | null;
  organizationId: string;
  organizationName: string | null;
  /** 'yyyy-MM-dd' */
  ownedFrom: string;
}

/**
 * The customer's personal account, finance summary, accrual and payment history: with `meteringPointId` the API answers
 * with that point's data only, without it as before GSB-8497.
 */
export interface CustomerMeteringPointParams {
  id: string;
  meteringPointId?: string;
}

export interface FinanceSummaryReadModel {
  rate: number;
  hasSubsidies: boolean;
  debt: number;
  overpayment: number;
  currentMonthCharged: number;
}

export interface AccrualHistoryEntryReadModel {
  period: string;
  openingBalance: number;
  charged: number;
  paid: number;
  subsidies: number;
  debt: number;
  overpayment: number;
  closingBalance: number;
  documentReference?: string;
}

export interface PaymentHistoryEntryReadModel {
  paymentDate: string;
  amount: number;
  method: string;
  effectivePeriod: string;
  documentReference?: string;
}

// TODO use shared (src/Distribution/Households/Billing/Frontend/WebClient/src/features/personalAccounts/types.ts)
export enum PersonalAccountStatusEnum {
  Undefined = 'Undefined',
  Enabled = 'Enabled',
  Disabled = 'Disabled',
  Liquidated = 'Liquidated',
}

export enum NotificationCategoryEnum {
  Transactional = 'Transactional',
  Marketing = 'Marketing',
  Survey = 'Survey',
}

export enum NotificationChannelEnum {
  Email = 'Email',
  Sms = 'Sms',
  Viber = 'Viber',
  Call = 'Call',
}

export interface ChannelPreferenceItem {
  category: NotificationCategoryEnum;
  channel: NotificationChannelEnum;
  enabled: boolean;
}

export interface ChannelPreferencesReadModel {
  items: ChannelPreferenceItem[];
}

/** POST /api/customers/{id}/segments/{segmentId}
 * DELETE /api/customers/{id}/segments/{segmentId}
 */
export interface AddSegmentToCustomerParams {
  id: string;
  segmentId: string;
}

export type SegmentFilterMode = 'Or' | 'And';

/** POST /api/customers/bulk/segments/add
 * POST /api/customers/bulk/segments/remove
 */
export interface SegmentsToCustomersBulkRequest {
  filter: {
    segmentIds: string[];
    segmentFilterMode: SegmentFilterMode;
  };
  segmentIds: string[];
}

/** GET /api/customers/{id}/segments */
export interface CustomerSegment {
  id: string;
  name: string;
  description: string;
  color: TagSegmentColorType;
  isActive: boolean;
}

/** POST /api/customers/{id}/segments */
export type AddSegmentToCustomerAddRequest = string[];
